import React, { useEffect, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import statisticsApi from 'api/modules/statisticsApi';

const PIPELINE_STEPS = [
  {
    key: 'not_picking_count',
    label: 'Not Picking',
    description: 'Shipments awaiting picking',
    icon: 'list-alt',
    color: '#e6a817',
  },
  {
    key: 'picking_count',
    label: 'Picking',
    description: 'Shipments in picking / picked',
    icon: 'tasks',
    color: '#27bcfd',
  },
  {
    key: 'packed_count',
    label: 'Packed',
    description: 'Packed, awaiting AWB',
    icon: 'box',
    color: '#2c7be5',
  },
  {
    key: 'ready_to_ship_count',
    label: 'Ready to Ship',
    description: 'AWB issued, ready for dispatch',
    icon: 'paper-plane',
    color: '#00d27a',
    hint: 'Dispatch via Manifests',
  },
];

const styles = {
  stepper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '2rem 1rem',
    gap: 0,
  },
  stepWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 100,
    position: 'relative',
  },
  circle: color => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: `${color}18`,
    border: `3px solid ${color}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  }),
  count: color => ({
    fontSize: '1.75rem',
    fontWeight: 700,
    color,
    marginBottom: 4,
  }),
  label: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#344050',
    marginBottom: 2,
    textAlign: 'center',
  },
  description: {
    fontSize: '0.75rem',
    color: '#9da9bb',
    textAlign: 'center',
  },
  hint: color => ({
    fontSize: '0.7rem',
    fontWeight: 600,
    color,
    marginTop: 8,
    padding: '3px 8px',
    borderRadius: 4,
    backgroundColor: `${color}12`,
    border: `1px solid ${color}40`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }),
  connector: color => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    paddingTop: 30,
    minWidth: 40,
  }),
  connectorLine: color => ({
    flex: 1,
    height: 3,
    background: `linear-gradient(90deg, ${color}, ${color}88)`,
    borderRadius: 2,
  }),
  connectorArrow: color => ({
    color,
    fontSize: '0.75rem',
    marginLeft: 2,
  }),
  unfulfillableCard: {
    borderLeft: '4px solid #e63757',
    backgroundColor: '#e6375708',
  },
};

const StepConnector = ({ fromColor, toColor }) => (
  <div style={styles.connector()}>
    <div
      style={{
        ...styles.connectorLine(),
        background: `linear-gradient(90deg, ${fromColor}, ${toColor})`,
      }}
    />
    <FontAwesomeIcon
      icon="chevron-right"
      style={styles.connectorArrow(toColor)}
    />
  </div>
);

const PipelineStep = ({ step, count }) => (
  <div style={styles.step}>
    <div style={styles.circle(step.color)}>
      <FontAwesomeIcon
        icon={step.icon}
        style={{ color: step.color, fontSize: '1.25rem' }}
      />
    </div>
    <div style={styles.count(step.color)}>{count}</div>
    <div style={styles.label}>{step.label}</div>
    <div style={styles.description}>{step.description}</div>
    {step.hint && (
      <div style={styles.hint(step.color)}>
        <FontAwesomeIcon icon="info-circle" className="me-1" />
        {step.hint}
      </div>
    )}
  </div>
);

const OrdersOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await statisticsApi.getProcessingOrderCount();
      setData(result);
    } catch (error) {
      if (error.response?.status !== 429) {
        toast.error('Failed to load order processing status.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-6">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!data) return null;

  const pipelineTotal = PIPELINE_STEPS.reduce(
    (sum, step) => sum + (data[step.key] || 0),
    0,
  );

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="bg-body-tertiary d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Order Processing Pipeline</h5>
          <span className="text-700 fs-10">
            Total in pipeline:{' '}
            <strong className="text-primary">{pipelineTotal}</strong>
          </span>
        </Card.Header>
        <Card.Body>
          <div style={styles.stepper}>
            {PIPELINE_STEPS.map((step, index) => (
              <React.Fragment key={step.key}>
                {index > 0 && (
                  <StepConnector
                    fromColor={PIPELINE_STEPS[index - 1].color}
                    toColor={step.color}
                  />
                )}
                <PipelineStep step={step} count={data[step.key] ?? 0} />
              </React.Fragment>
            ))}
          </div>
        </Card.Body>
      </Card>

      {(data.unfulfillable_count ?? 0) > 0 && (
        <Card className="mb-3" style={styles.unfulfillableCard}>
          <Card.Body className="d-flex align-items-center py-2">
            <FontAwesomeIcon
              icon="exclamation-triangle"
              className="text-danger me-2"
            />
            <span className="text-700 me-1">Unfulfillable:</span>
            <strong className="text-danger me-2">
              {data.unfulfillable_count}
            </strong>
            <small className="text-600">
              shipments not linked to products/batches
            </small>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default OrdersOverview;
